import { get } from 'styled-system'

/* Using the CJS entry point due to https://github.com/webpack/webpack/issues/6584 */
const merge = require('deepmerge')

import { useTheme } from '../useTheme'

const grayGhostStyle = {
  color: 'inherit',
  _hover: {
    bg: 'gray.100',
  },
  _active: {
    bg: 'gray.200',
  },
}

const whiteGhostStyle = {
  color: 'inherit',
  _hover: {
    bg: 'whiteAlpha.800',
  },
  _active: {
    bg: 'whiteAlpha.600',
  },
}

const ghostVariantProps = ({ color }) => {
  let result

  if (color === 'gray') {
    result = grayGhostStyle
  } else if (color === 'white') {
    result = whiteGhostStyle
  } else {
    result = {
      color: `${color}.500`,
      bg: 'transparent',
      _hover: {
        bg: `${color}.50`,
      },
      _active: {
        bg: `${color}.100`,
      },
    }
  }

  return result
}

////////////////////////////////////////////////////////////

const outlineVariantProps = props => {
  const { color, hasDepth, fontFamily, isDisabled } = props
  const borderColor = 'gray.200'

  const style = {
    fontFamily: 'heading2',
    pt: '0.1rem', // On Buttons with heading2 (DogmaBlack), this fixes the vertical alignment
    borderRadius: 'lg',
    border: '2px',
    borderBottom: hasDepth && !isDisabled ? '1px' : '2px',
    borderColor: color === 'gray' ? borderColor : 'current',
    ...ghostVariantProps(props),
    backgroundColor: 'white',
  }

  return withDepthProps(style, props)
}

////////////////////////////////////////////////////////////

const graySolidStyle = {
  bg: 'gray.100',
  color: 'blackAlpha.700',
  _hover: {
    bg: 'gray.200',
  },
  _active: {
    bg: 'gray.300',
  },
}

const whiteSolidStyle = {
  bg: 'white',
  color: 'pink.500',
  _hover: {
    bg: 'whiteAlpha.900',
  },
  _active: {
    bg: 'whiteAlpha.800',
  },
}

const solidVariantProps = props => {
  const { color } = props

  const borderColor = 'gray.200'

  let style: any = {
    bg: `${color}.500`,
    color: 'white',
    fontFamily: 'heading2',
    pt: '0.1rem', // On Buttons with heading2 (DogmaBlack), this fixes the vertical alignment
    borderRadius: 'lg',
    border: '2px',
    borderColor: `${color}.500`,
    _hover: {
      bg: `${color}.600`,
      borderColor: `${color}.600`,
    },
    _active: {
      bg: `${color}.700`,
      borderColor: `${color}.700`,
    },
  }

  if (color === 'gray') {
    style = merge(style, graySolidStyle)
  }

  if (color === 'white') {
    style = merge(style, whiteSolidStyle)
  }

  return withDepthProps(style, props)
}

////////////////////////////////////////////////////////////

const linkVariantProps = ({ color }) => {
  return {
    p: 0,
    height: 'auto',
    lineHeight: 'normal',
    color: `${color}.500`,
    _hover: {
      textDecoration: 'underline',
    },
    _active: {
      color: `${color}.700`,
    },
  }
}

////////////////////////////////////////////////////////////

const disabledProps = {
  _disabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
}

////////////////////////////////////////////////////////////

const sizes = {
  lg: {
    height: 12,
    minWidth: 12,
    fontSize: 'md',
    px: 8,
  },
  md: {
    height: 10,
    minWidth: 10,
    fontSize: 'sm',
    px: 6,
  },
  sm: {
    height: 8,
    minWidth: 8,
    fontSize: 'xs',
    px: 4,
  },
  xs: {
    height: 6,
    minWidth: 6,
    fontSize: 'xs',
    px: 3,
  },
}

const sizeProps = ({ size, variant }) => {
  if (variant === 'unstyled') return {}
  return sizes[size]
}

////////////////////////////////////////////////////////////

const getWhiteDepthStyle = theme => ({
  boxShadow: `0 0.2rem ${get(theme.colors, 'gray.400')}`,
  _active: {
    boxShadow: `0 0.1rem ${get(theme.colors, 'gray.600')}`,
    transform: 'translateY(0.1rem)',
  },
})

const getGrayDepthStyle = theme => ({
  boxShadow: `0 0.2rem ${get(theme.colors, 'whiteAlpha.800')}`,
  _active: {
    boxShadow: `0 0.1rem ${get(theme.colors, 'whiteAlpha.600')}`,
    transform: 'translateY(0.1rem)',
  },
})

const depthProps = ({ hasDepth, color, theme }) => {
  if (!hasDepth) return {}

  if (color === 'gray') {
    return getGrayDepthStyle(theme)
  }
  if (color === 'white') {
    return getWhiteDepthStyle(theme)
  }

  return {
    boxShadow: `0 0.2rem ${get(theme.colors, `${color}.600`, color)}`,
    _active: {
      boxShadow: `0 0.1rem ${get(theme.colors, `${color}.800`, color)}`,
      transform: 'translateY(0.1rem)',
    },
  }
}

const withDepthProps = (styleProps, props) => merge(depthProps(props), styleProps)

////////////////////////////////////////////////////////////

const focusProps = {
  _focus: {
    boxShadow: 'outline',
  },
}

////////////////////////////////////////////////////////////

const unstyledStyle = {
  userSelect: 'inherit',
  bg: 'none',
  border: 0,
  borderRadius: 0,
  color: 'inherit',
  display: 'inline-flex',
  font: 'inherit',
  lineHeight: 'inherit',
  m: 0,
  p: 0,
  textAlign: 'inherit',
  fontFamily: 'inherit',
  textTransform: 'none',
  height: 'auto',
}

////////////////////////////////////////////////////////////

const variantProps = props => {
  switch (props.variant) {
    case 'solid':
      return solidVariantProps(props)
    case 'ghost':
      return ghostVariantProps(props)
    case 'link':
      return linkVariantProps(props)
    case 'outline':
      return outlineVariantProps(props)
    case 'unstyled':
      return unstyledStyle
    default:
      return {}
  }
}

////////////////////////////////////////////////////////////

const borderRadiusSizes = {
  lg: '3rem',
  md: '2.5rem',
  sm: '2rem',
  xs: '1.5rem',
}

const roundProps = props => {
  if (!props.isRound) return {}
  return {
    borderRadius: borderRadiusSizes[props.size],
  }
}

////////////////////////////////////////////////////////////

const baseProps = {
  display: 'inline-flex',
  appearance: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 250ms',
  userSelect: 'none',
  position: 'relative',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  lineHeight: '1.2',
  outline: 'none',
  fontFamily: 'heading',
  textTransform: 'uppercase',
}

////////////////////////////////////////////////////////////

export const useButtonStyle = props => {
  const theme = useTheme()

  const _props = { ...props, theme }
  return {
    ...baseProps,
    ...sizeProps(_props),
    ...focusProps,
    ...disabledProps,
    ...variantProps(_props),
    ...roundProps(_props),
  }
}

export default useButtonStyle
